
import React, { useState, useEffect, useMemo } from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import { AnalysisMetrics } from '../types';
import { MASTER_METRICS } from '../services/viewPolicy';
import { useGuitarMatch } from '../context/GuitarMatchContext';
import { isMetricActive, isTabUnlocked } from '../services/tabUnlockPolicy';

interface MetricsChartProps {
  metrics: AnalysisMetrics;
  overallScore: number | null; 
  lang?: 'en' | 'fr';
  levelId?: number;
  activeTab?: string;
}

export const MetricsChart: React.FC<MetricsChartProps> = ({ metrics, overallScore, lang = 'en', levelId = 4, activeTab }) => {
  const { accessibilityMode, result } = useGuitarMatch();
  const [isMobile, setIsMobile] = useState(false);
  const isHighContrast = accessibilityMode === 'HIGH_CONTRAST';

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check(); 
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const calibStatus = result?.session?.truth_report?.calibration_status || "VALID";
  
  const chartData = useMemo(() => {
    const m = metrics || {} as AnalysisMetrics;

    // NO-SIM + UI DYNAMICS: 
    // On ne montre que les métriques visibles pour ce niveau (évite le radar vide)
    return MASTER_METRICS
      .filter(mDef => mDef.id !== 'audio' && isTabUnlocked(levelId || 1, mDef.id)) 
      .map(mDef => {
        const rawVal = (m as any)[mDef.key] ?? (m as any)[mDef.id];
        const val = typeof rawVal === 'number' ? rawVal : 0;
        
        // Double check technique
        const isActive = isMetricActive(mDef.id, levelId || 1, calibStatus);
        
        return {
            subject: lang === 'fr' ? mDef.label.fr : mDef.label.en,
            value: isActive ? val : 0, 
            fullMark: 100,
            isHighlighted: mDef.id === activeTab,
            id: mDef.id,
            isActive
        };
      });
  }, [metrics, lang, activeTab, levelId, calibStatus]);

  const bgClass = isHighContrast ? 'bg-black border-2 border-white' : 'bg-[#050505]';
  const gridStroke = isHighContrast ? '#ffffff' : 'rgba(255,255,255,0.05)';
  const radarFill = isHighContrast ? 'none' : 'url(#radarFill)';
  const radarStroke = isHighContrast ? '#ffff00' : (overallScore !== null && overallScore < 35 ? "#64748b" : "#FF6261");

  // Sécurité: Si moins de 3 points, le Radar ne peut pas se dessiner. 
  // On s'assure d'avoir au moins les 3 fondamentaux du L1.
  if (chartData.length < 3) return null;

  return (
    <div className={`w-full h-full relative flex flex-col overflow-hidden transition-all duration-700 ${bgClass}`}>
      <div className="pt-6 px-6 shrink-0 text-left">
        <h3 className="text-[9px] font-black uppercase tracking-[0.4em] flex items-center gap-3 text-white/40">
            <div className={`w-1.5 h-1.5 rounded-full ${activeTab && activeTab !== 'report' ? 'bg-smart-accent animate-ping' : 'bg-white/20'}`}></div>
            Neural <span className="text-white italic">Fingerprint</span>
        </h3>
      </div>
      
      <div className="flex-1 w-full min-h-[300px] relative">
          <ResponsiveContainer width="100%" height="100%">
              <RadarChart cx="50%" cy="50%" outerRadius={isMobile ? "55%" : "65%"} data={chartData}>
                <defs>
                  <linearGradient id="radarFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF6261" stopOpacity={0.4}/>
                    <stop offset="100%" stopColor="#DA9000" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <PolarGrid stroke={gridStroke} strokeWidth={1} />
                <PolarAngleAxis 
                  dataKey="subject" 
                  tick={({ payload, x, y, index }) => {
                    const item = chartData[index];
                    return (
                      <text 
                        x={x} y={y} dy={4} 
                        textAnchor="middle" 
                        fill={item?.isHighlighted ? "#FF6261" : (item?.isActive ? "#94a3b8" : "#334155")} 
                        fontSize={8} 
                        fontWeight={900} 
                        className="uppercase tracking-widest transition-all duration-500"
                      >
                        {payload.value}
                      </text>
                    );
                  }} 
                />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                <Radar 
                  name="Performance" 
                  dataKey="value" 
                  stroke={radarStroke} 
                  strokeWidth={2} 
                  fill={radarFill} 
                  fillOpacity={0.6} 
                  isAnimationActive={true}
                />
              </RadarChart>
          </ResponsiveContainer>
      </div>
    </div>
  );
};
